import { Actions, Getters, Mutations, MutationTypes, State } from './NewsList.types';
import { ActionTree, MutationTree } from 'vuex';
import { NewsListItem, NewsCategories } from '@app/scripts/api/models/NewsListItem';
import Api from '@app/scripts/api';
import orderBy from 'lodash/orderBy';

export const STATES = {
    INIT: Symbol('INIT'),
    FETCHING: Symbol('FETCHING'),
    FETCHED: Symbol('FETCHED'),
};

const state: State = {
    newsList: null,
    newsListConstant: null,
    state: STATES.INIT,
    collectionId: null,
    categories: null,
};

function filterNewsCategories (newslistConstant: NewsListItem, isPublished: boolean): Array<NewsCategories> {
    const categories = newslistConstant
        .filter(news => news.isPublished === isPublished)
        .filter(news => news.categories.length > 0)
        .flatMap(news => news.categories);

    const uniqueCategories = [...new Map(categories.map((category) => [category['uid'], category])).values()];

    return uniqueCategories;
}

const actions: ActionTree<State, State> & Actions = {
    async fetchNews ({ commit }, payload) {
        commit(MutationTypes.FETCHING, payload.collectionId);

        const result: Array<NewsListItem> = await Api.version(1).get<Array<NewsListItem>>(
            `rooms/news/${payload.collectionId}`,
            {
                params: {
                    limit: payload.limit,
                    excludeInvisible: payload.excludeInvisible,
                    category: payload.category,
                },
            }
        );

        commit(MutationTypes.FETCHED, result);
    },

    async toggleNewsActive ({ commit, state }, payload) {
        if (state.newsList === null) return;

        await Api.version(1).post(`rooms/news/${state.collectionId}/items/${payload.uid}/active`, {
            active: !payload.active,
        }).then((data) => {
            commit(MutationTypes.UPDATE_NEWS_LIST, data);
        });
    },
};

const getters: Getters = {
    activeNewsCategories (state: State): Array<NewsCategories> {
        if (state.newsListConstant === null) {
            return [];
        }
        return filterNewsCategories(state.newsListConstant, true);
    },
    inactiveNewsCategories (state: State): Array<NewsCategories> {
        if (state.newsListConstant === null) {
            return [];
        }
        return filterNewsCategories(state.newsListConstant, false);
    },
    newsList (state: State): Array<NewsListItem> | null {
        return state.newsList;
    },

    inactiveNewsList (state: State): Array<NewsListItem> | null {
        if (state.newsList === null) {
            return null;
        }
        const result = state.newsList.filter(item => item.isPublished === false);
        // Sorted by news.startDate descending, sorting is done in NewsRepository.php
        return result;
    },

    activeNewsList (state: State): Array<NewsListItem> | null {
        if (state.newsList === null) {
            return null;
        }

        const result = state.newsList.filter(item => item.isPublished === true);
        // Sorted by news.startDate descending, sorting is done in NewsRepository.php
        return result;
    },

    state (state: State) {
        return state.state;
    },
};

const mutations: MutationTree<State> & Mutations = {
    FETCHING (state, collectionId) {
        state.state = STATES.FETCHING;
        if (collectionId) {
            state.collectionId = collectionId;
        }
    },

    FETCHED (state, list: Array<NewsListItem>) {
        state.state = STATES.FETCHED;
        state.newsList = list;
        // Only set the newslistConstant
        if (state.newsListConstant === null) {
            state.newsListConstant = list;
        }
    },
    UPDATE_NEWS_LIST (state, data: NewsListItem): void {
        if (state.newsList === null) return;

        const newsList = state.newsList.find(item => item.uid === data.uid);
        const newsListConstant = newsList;
        // We use Object.assign here, as just changing the item would not update the computed state
        Object.assign(newsList, data);
        Object.assign(newsListConstant, data);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
